import { v4 } from "uuid";
import type { AgentRunRequest, Event, IMessage, SendContent } from "@/types";
import {
  buildAdkUserMessage,
  buildAdkUserParts,
  dedupeAdkMessages,
  insertMissingAdkSessionEvents,
  mapAdkEventToMessages,
  mapAdkSessionEventsToMessages,
  pairFunctionResponses,
} from "../protocol";
import type {
  AdkConversationController,
  AdkConversationOptions,
  AdkConversationState,
} from "./types";

const DEFAULT_PAGE_SIZE = 10;

/**
 * 创建 controller 的初始状态。
 * 这里不读取任何外部配置，确保 runtime 可以在 React、UMD、单测环境中复用。
 */
const createInitialState = (
  sessionId?: string,
  pageSize = DEFAULT_PAGE_SIZE,
): AdkConversationState => ({
  appInfo: null,
  initialized: false,
  loading: false,
  sessionLoading: false,
  currentSessionId: sessionId || v4(),
  messages: [],
  sessionList: [],
  sessionPagination: {
    total: 0,
    pageNum: 1,
    pageSize,
  },
  prologue: "",
  suggestions: [],
});

/**
 * 默认请求体构造器，适配当前 xgroup openApi 的 sendMsgStreaming 入参。
 * 业务项目可通过 options.requestBuilder 注入 modelCode、agentNo、taskMode 等字段。
 */
const defaultRequestBuilder = ({
  content,
  sessionId,
  stateDelta,
}: {
  content: SendContent;
  sessionId: string;
  stateDelta?: Record<string, unknown>;
}): AgentRunRequest => ({
  stream: true,
  sessionId,
  content: {
    role: "user",
    parts: buildAdkUserParts(content),
  },
  files: (content.files || []).map((file) => ({
    fileName: file.fileName || file.response?.fileName,
    fileId: file.fileId || file.response?.fileId,
    tempUrl: file.tempUrl || file.response?.fileUrl || file.response?.tempUrl,
    type: file.type || file.response?.fileType,
    mimeType: file.mimeType || file.response?.mimeType,
  })),
  ...(stateDelta !== undefined ? { stateDelta } : {}),
});

/**
 * 创建无 React 依赖的 ADK 对话数据流控制器。
 *
 * controller 只管理状态和数据流编排；所有请求细节都交给 transport，
 * 所有 UI 提示都通过 callbacks 抛给调用方。
 */
export const createAdkConversationController = (
  options: AdkConversationOptions,
): AdkConversationController => {
  const {
    transport,
    callbacks,
    protocol,
    requestBuilder = defaultRequestBuilder,
    createFunctionResponse = (fnCall, confirmed) => ({
      id: fnCall.id,
      name: fnCall.name,
      response: { confirmed },
    }),
  } = options;

  let state = createInitialState(options.sessionId, options.session?.pageSize);
  let ctrl: AbortController | null = null;
  let pollingTimer: ReturnType<typeof setInterval> | null = null;
  let textMessage: IMessage | null = null;
  let stateDelta: Record<string, unknown> | undefined;
  let disposed = false;
  const subscribers = new Set<(nextState: AdkConversationState) => void>();

  /** 通知所有订阅者。React Hook、UMD 调用方都通过 subscribe 接收状态变化。 */
  const emit = () => {
    subscribers.forEach((subscriber) => subscriber(state));
  };

  const setState = (
    patch:
      | Partial<AdkConversationState>
      | ((current: AdkConversationState) => AdkConversationState),
  ) => {
    state =
      typeof patch === "function"
        ? patch(state)
        : {
            ...state,
            ...patch,
          };
    emit();
  };

  const setMessages = (
    messages: IMessage[] | ((prev: IMessage[]) => IMessage[]),
  ) => {
    setState((current) => ({
      ...current,
      // 所有入口写入 messages 时都统一做工具响应配对和文本去重。
      messages:
        typeof messages === "function"
          ? pairFunctionResponses(dedupeAdkMessages(messages(current.messages)))
          : pairFunctionResponses(dedupeAdkMessages(messages)),
    }));
  };

  const appendMessages = (messages: IMessage[]) => {
    if (!messages.length) return;
    setMessages((prev) => [...prev, ...messages]);
  };

  const updateLastBotMeta = (event: Event) => {
    if (!event.modelCode && !event.usageMetadata && !event.finishReason) return;
    setMessages((prev) => {
      const next = [...prev];
      const index = next.findLastIndex((message) => message.role === "bot");
      if (index < 0) return prev;
      next[index] = {
        ...next[index],
        modelCode: event.modelCode ?? next[index].modelCode,
        usageMetadata: event.usageMetadata ?? next[index].usageMetadata,
        finishReason: event.finishReason ?? next[index].finishReason,
        raw: {
          ...(next[index].raw || {}),
          ...event,
        },
      };
      return next;
    });
  };

  /**
   * 处理单个 stream event。
   *
   * 对纯文本分片做流式拼接；遇到 fileData、functionCall、代码执行等非文本内容时，
   * 直接作为独立消息插入，避免破坏工具类消息的渲染结构。
   */
  const processEvent = (event: Event, requestStream = true) => {
    callbacks?.onStream?.(event);
    updateLastBotMeta(event);

    if (event.content?.role === "followup") {
      const text = event.content.parts?.[0]?.text;
      if (text) {
        setState((current) => ({
          ...current,
          suggestions: [...current.suggestions, text],
        }));
      }
      return;
    }

    if (event.content?.parts?.length) {
      const messages = mapAdkEventToMessages(event, protocol);
      messages.forEach((message) => {
        const isPureText =
          typeof message.text === "string" &&
          !message.fileData?.length &&
          !message.functionCall &&
          !message.functionResponse &&
          !message.executableCode &&
          !message.codeExecutionResult;

        if (isPureText && requestStream) {
          if (event.partial === false) {
            textMessage = null;
            return;
          }
          const isNewInvocation =
            textMessage?.invocationId !== message.invocationId;
          if (
            !textMessage ||
            isNewInvocation ||
            textMessage.thought !== message.thought
          ) {
            textMessage = message;
            appendMessages([message]);
            return;
          }

          textMessage = {
            ...textMessage,
            ...message,
            text: `${textMessage.text || ""}${message.text || ""}`,
          };
          setMessages((prev) =>
            prev.map((item) =>
              item.id === textMessage!.id ? textMessage! : item,
            ),
          );
          if (event.finishReason === "stop") textMessage = null;
          return;
        }

        textMessage = null;
        appendMessages([message]);
      });
      return;
    }

    appendMessages(mapAdkEventToMessages(event, protocol));
  };

  const loadSessions = async () => {
    if (!transport.fetchSessions) return;
    setState({ sessionLoading: true });
    try {
      const response = await transport.fetchSessions({
        pageNum: state.sessionPagination.pageNum,
        pageSize: state.sessionPagination.pageSize,
      });
      setState({
        sessionList: response.data || [],
        sessionPagination: {
          ...state.sessionPagination,
          total: response.pagination?.total || 0,
        },
      });
      if (options.session?.showFirstSession && response.data?.[0]?.sessionId) {
        await controller.setCurrentSession(response.data[0].sessionId);
      }
    } catch (error) {
      callbacks?.onError?.(error);
    } finally {
      setState({ sessionLoading: false });
    }
  };

  /**
   * session events 轮询用于补齐后端异步产生的消息。
   * 当前回复还在生成时不插入，避免流式拼接过程中消息数组结构被打乱。
   */
  const startSessionPolling = () => {
    if (
      !options.session?.pollSessionEvents ||
      !transport.fetchSessionEvents ||
      pollingTimer
    )
      return;

    const interval = Math.max(
      options.session.sessionEventsPollingInterval || 10000,
      1000,
    );
    pollingTimer = setInterval(() => {
      if (disposed || state.loading || !state.currentSessionId) return;
      transport
        .fetchSessionEvents!(state.currentSessionId)
        .then((events) =>
          controller.syncSessionEvents(events, state.currentSessionId),
        )
        .catch((error) => callbacks?.onError?.(error));
    }, interval);
  };

  const controller: AdkConversationController = {
    getSnapshot: () => state,
    subscribe: (subscriber) => {
      subscribers.add(subscriber);
      return () => {
        subscribers.delete(subscriber);
      };
    },
    init: async () => {
      if (disposed) return;
      setState({ sessionLoading: true });
      try {
        const appInfo = await transport.fetchAppConfig?.();
        setState({
          appInfo: appInfo || null,
          prologue: appInfo?.onboardingInfo?.prologue || "",
          suggestions: appInfo?.onboardingInfo?.suggested_questions || [],
          initialized: true,
        });
        await loadSessions();
        startSessionPolling();
      } catch (error) {
        callbacks?.onError?.(error);
        setState({ initialized: true });
      } finally {
        setState({ sessionLoading: false });
      }
    },
    destroy: () => {
      disposed = true;
      ctrl?.abort();
      if (pollingTimer) clearInterval(pollingTimer);
      pollingTimer = null;
      subscribers.clear();
    },
    startChat: async (content) => {
      if (state.loading) return;
      if (
        !content.text?.trim() &&
        !content.files?.length &&
        !content.functionResponse
      )
        return;

      ctrl?.abort();
      ctrl = new AbortController();
      textMessage = null;
      setState({ loading: true, suggestions: [] });
      appendMessages(buildAdkUserMessage(content));

      const payload = requestBuilder({
        content,
        sessionId: state.currentSessionId,
        stateDelta,
      });
      const requestStream = payload.stream !== false;

      try {
        await transport.sendMessageStreaming(payload, {
          signal: ctrl.signal,
          onEvent: (event) => processEvent(event, requestStream),
          onError: callbacks?.onError,
          onClose: callbacks?.onRequestComplete,
        });
      } catch (error) {
        if ((error as any)?.name !== "AbortError") callbacks?.onError?.(error);
      } finally {
        setState({ loading: false });
        const completedMessage = textMessage as IMessage | null;
        if (completedMessage) {
          callbacks?.onMessage?.(completedMessage.text || "", completedMessage);
        }
        textMessage = null;
      }
    },
    stopChat: async () => {
      try {
        await transport.interrupt?.(state.currentSessionId);
      } catch {
        ctrl?.abort();
      }
      ctrl?.abort();
      textMessage = null;
      setState({ loading: false });
    },
    reChat: async () => {
      const lastUserIndex = state.messages.findLastIndex(
        (message) => message.role === "user" && !message.functionResponse,
      );
      if (lastUserIndex < 0) return;
      const lastUserMessage = state.messages[lastUserIndex];
      // 先截断最后一条用户消息及其后的回复，再复用 startChat 重新插入用户消息并发起请求。
      const nextMessages = state.messages.slice(0, lastUserIndex);
      setMessages(nextMessages);
      await controller.startChat({
        text: lastUserMessage.text || "",
        files: (lastUserMessage.fileData || []).map((file) => ({
          fileName: file.displayName,
          tempUrl: file.fileUri,
          mimeType: file.mimeType,
        })) as any,
      });
    },
    clearChat: () => {
      ctrl?.abort();
      textMessage = null;
      setState({
        currentSessionId: v4(),
        messages: [],
        loading: false,
        prologue: state.appInfo?.onboardingInfo?.prologue || "",
        suggestions: state.appInfo?.onboardingInfo?.suggested_questions || [],
      });
    },
    suggestChat: async (suggestion) => {
      await controller.startChat({ text: suggestion });
    },
    confirmFnCall: async (fnCall, confirmed) => {
      await controller.startChat({
        functionResponse: createFunctionResponse(fnCall, confirmed),
      });
    },
    switchSession: async (sessionId) => controller.setCurrentSession(sessionId),
    setCurrentSession: async (sessionId) => {
      if (!sessionId || sessionId === state.currentSessionId) return;
      ctrl?.abort();
      textMessage = null;
      setState({
        currentSessionId: sessionId,
        loading: false,
        messages: [],
        suggestions: [],
      });
      if (!transport.fetchSessionEvents) return;
      setState({ sessionLoading: true });
      try {
        const events = await transport.fetchSessionEvents(sessionId);
        controller.replaceSessionEvents(events);
      } catch (error) {
        callbacks?.onError?.(error);
      } finally {
        setState({ sessionLoading: false });
      }
    },
    deleteSession: async (sessionId) => {
      const success = (await transport.closeSession?.(sessionId)) ?? false;
      if (success) {
        setState((current) => ({
          ...current,
          sessionList: current.sessionList.filter(
            (session) => session.sessionId !== sessionId,
          ),
        }));
        if (sessionId === state.currentSessionId) controller.clearChat();
      }
      return success;
    },
    updateSession: async (sessionId, title) => {
      const success =
        (await transport.updateSessionTitle?.(sessionId, title)) ?? false;
      if (success) {
        setState((current) => ({
          ...current,
          sessionList: current.sessionList.map((session) =>
            session.sessionId === sessionId ? { ...session, title } : session,
          ),
        }));
      }
      return success;
    },
    likeMessage: transport.feedback,
    replaceSessionEvents: (events) => {
      // 切换历史会话时用服务端完整事件重建消息列表。
      setMessages(mapAdkSessionEventsToMessages(events, protocol));
    },
    syncSessionEvents: (events, sessionId) => {
      if (sessionId && sessionId !== state.currentSessionId) return;
      // 轮询增量同步时只补齐页面尚未展示的模型事件。
      setMessages((prev) =>
        insertMissingAdkSessionEvents(prev, events, protocol),
      );
    },
    setMessages,
    setStateDelta: (delta) => {
      stateDelta = delta;
    },
  };

  return controller;
};
